import React, { useState } from 'react';
import { ArrowDownUp, Clock, Calendar, Check, X, Cpu, RefreshCw } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import TimezoneSelector from './TimezoneSelector';

/**
 * Компонент формы конвертера Unix-времени
 */
const ConverterForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isConverting, 
  handleConvert,
  russianTimezones,
  currentTime
}) => {
  
  // Локальное состояние для отображения миллисекунд
  const [currentUnixTime, setCurrentUnixTime] = useState({
    seconds: Math.floor(Date.now() / 1000),
    milliseconds: Math.floor(Date.now())
  });
  
  // Обновление текущего Unix-времени
  React.useEffect(() => {
    setCurrentUnixTime({
      seconds: Math.floor(currentTime / 1000),
      milliseconds: currentTime
    });
  }, [currentTime]);
  
  // Обработчик изменения полей ввода
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => ({
      ...prev,
      [name]: fieldValue
    }));

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Обработчик смены типа конвертации
  const toggleConversionType = () => {
    setFormData(prev => ({
      ...prev,
      conversionType: prev.conversionType === 'unix-to-human' ? 'human-to-unix' : 'unix-to-human'
    }));
  };

  // Вставка текущего времени
  const insertCurrentTime = () => {
    if (formData.conversionType === 'unix-to-human') {
      setFormData(prev => ({
        ...prev,
        unixTimestamp: prev.includeMilliseconds ? 
          currentUnixTime.milliseconds.toString() : 
          currentUnixTime.seconds.toString()
      }));
    } else {
      const now = new Date().toISOString().slice(0, 16);
      setFormData(prev => ({
        ...prev,
        humanDate: now
      }));
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    handleConvert();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Cpu className="w-6 h-6 text-cyan-500" />
          Конвертер Unix-времени
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Переключатель типа конвертации */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Тип конвертации</div>
            <button
              type="button"
              onClick={toggleConversionType}
              className="flex items-center text-sm text-cyan-600 dark:text-cyan-400 hover:text-cyan-800 dark:hover:text-cyan-300 transition-colors"
            >
              <ArrowDownUp className="w-4 h-4 mr-1" />
              Поменять местами
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, conversionType: 'unix-to-human' }))}
              className={`p-3 text-sm rounded-lg border transition-colors flex flex-col items-center justify-center ${
                formData.conversionType === 'unix-to-human' 
                  ? 'bg-cyan-100 border-cyan-300 text-cyan-700 dark:bg-cyan-900/30 dark:border-cyan-700 dark:text-cyan-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              <Cpu className="w-5 h-5 mb-2" />
              <div className="font-medium">Unix → Дата</div>
              <div className="text-xs opacity-75 mt-1">Преобразовать timestamp в дату</div>
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, conversionType: 'human-to-unix' }))}
              className={`p-3 text-sm rounded-lg border transition-colors flex flex-col items-center justify-center ${
                formData.conversionType === 'human-to-unix' 
                  ? 'bg-cyan-100 border-cyan-300 text-cyan-700 dark:bg-cyan-900/30 dark:border-cyan-700 dark:text-cyan-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              <Calendar className="w-5 h-5 mb-2" />
              <div className="font-medium">Дата → Unix</div>
              <div className="text-xs opacity-75 mt-1">Преобразовать дату в timestamp</div>
            </button>
          </div>
        </div>

        {/* Поля ввода в зависимости от типа конвертации */}
        {formData.conversionType === 'unix-to-human' ? (
          <div className="mb-6">
            <label htmlFor="unixTimestamp" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <Cpu className="w-4 h-4 mr-2 text-cyan-500" />
              Unix Timestamp {formData.includeMilliseconds ? '(в миллисекундах)' : '(в секундах)'}
            </label>
            <div className="flex">
              <input
                type="text"
                id="unixTimestamp"
                name="unixTimestamp"
                value={formData.unixTimestamp}
                onChange={handleInputChange}
                className={`form-input w-full ${errors.unixTimestamp ? 'input-error' : ''}`}
                placeholder={formData.includeMilliseconds ? "Например: 1617184712123" : "Например: 1617184712"}
              />
              <button
                type="button"
                onClick={insertCurrentTime}
                className="ml-2 p-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex-shrink-0"
                title="Вставить текущее время"
              >
                <Clock className="w-5 h-5" />
              </button>
            </div>
            {errors.unixTimestamp && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.unixTimestamp}
              </p>
            )}
            <div className="mt-2 bg-gray-50 dark:bg-gray-800 p-3 rounded-md border border-gray-200 dark:border-gray-700 text-sm">
              <div className="text-gray-700 dark:text-gray-300 flex items-center justify-between">
                <span>Текущее Unix-время:</span>
                <code className="font-mono bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-cyan-700 dark:text-cyan-300">
                  {formData.includeMilliseconds 
                    ? currentUnixTime.milliseconds 
                    : currentUnixTime.seconds}
                </code>
              </div>
            </div>
          </div>
        ) : (
          <div className="mb-6">
            <label htmlFor="humanDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <Calendar className="w-4 h-4 mr-2 text-cyan-500" />
              Дата и время
            </label>
            <div className="flex">
              <input
                type="datetime-local"
                id="humanDate"
                name="humanDate"
                value={formData.humanDate}
                onChange={handleInputChange}
                className={`form-input w-full ${errors.humanDate ? 'input-error' : ''}`}
              />
              <button
                type="button"
                onClick={insertCurrentTime}
                className="ml-2 p-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex-shrink-0"
                title="Вставить текущее время"
              >
                <Clock className="w-5 h-5" />
              </button>
            </div>
            {errors.humanDate && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.humanDate}
              </p>
            )}
          </div>
        )}

        {/* Выбор часового пояса */}
        <TimezoneSelector 
          timezone={formData.timezone}
          setTimezone={(timezone) => setFormData(prev => ({ ...prev, timezone }))}
          timezones={russianTimezones}
        />

        {/* Настройки отображения */}
        <div className="mb-6 mt-6">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Настройки</h3>
          <div className="space-y-2">
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                name="includeMilliseconds"
                checked={formData.includeMilliseconds}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Использовать миллисекунды
              </span>
            </label>
            
            <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
              <div className="text-sm text-gray-700 dark:text-gray-300 mb-2">Отображать форматы:</div>
              <div className="grid grid-cols-2 gap-2">
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    name="showISO"
                    checked={formData.showISO}
                    onChange={handleInputChange}
                    className="form-checkbox"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    ISO 8601
                  </span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    name="showRFC"
                    checked={formData.showRFC}
                    onChange={handleInputChange}
                    className="form-checkbox"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    RFC 2822
                  </span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    name="showLocale"
                    checked={formData.showLocale}
                    onChange={handleInputChange}
                    className="form-checkbox"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    Локализованный
                  </span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    name="showRelative"
                    checked={formData.showRelative}
                    onChange={handleInputChange}
                    className="form-checkbox"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    Относительный
                  </span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Кнопка конвертации */}
        <div className="flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<ArrowDownUp className="w-5 h-5" />}
            disabled={isConverting}
            isLoading={isConverting}
            size="lg"
          >
            {isConverting ? 'Выполняется конвертация...' : 'Конвертировать'}
          </Button>
          <Button
            type="button"
            onClick={insertCurrentTime}
            icon={<Clock className="w-5 h-5" />}
            variant="secondary"
            disabled={isConverting}
            size="lg"
          >
            Текущее время
          </Button>
        </div>
      </div>
    </form>
  );
};

export default ConverterForm;